import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchInvoicePendingCountReport } from "../../../state/reducers/invoicePendingCountSlice";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";

// The component for the Invoice Pending Count Chart
const InvoicePendingCountChart = () => {
  const dispatch = useDispatch();
  const { data, status, error } = useSelector((state) => state.invoicePendingReport);
  
  const [groupBy, setGroupBy] = useState("month"); // Default grouping: month
  const [reportType, setReportType] = useState("Analytics"); // Default report type: Analytics

  useEffect(() => {
    dispatch(fetchInvoicePendingCountReport(groupBy)); // Fetch data for the selected group (month/year)
  }, [dispatch, groupBy]);

  // Prepare chart data based on the fetched data
  const getChartData = () => {
    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ];
    if (status === "succeeded" && data) {
        let labels = months;
        let invoiceCounts = new Array(12).fill(0); // Default to zero for all months  
        if (groupBy === "month" && data.monthly_invoice_count) {
            // Reset invoice counts to zero
            // Populate the invoiceCounts array for each month
            data.monthly_invoice_count.forEach((record) => {
              record.monthly_invoice_count.forEach((monthData) => {
                const monthIndex = months.indexOf(monthData.month);
                if (monthIndex !== -1) {
                  invoiceCounts[monthIndex] = monthData.invoice_count;
                }
              });
            });
          }

      if (groupBy === "year" && data.yearly_invoice_count) {
        labels = data.yearly_invoice_count.map((item) => item.year);
        invoiceCounts = data.yearly_invoice_count.map((item) => item.invoice_count);
      }

      return {
        labels,
        datasets: [
          {
            label: `Invoice Pending Count (${groupBy})`,
            data: invoiceCounts,
            backgroundColor: "#1e40af", // Dark Blue color
            borderColor: "#00008B", // Dark Blue border
            borderWidth: 1,
          },
        ],
      };
    }

    return {
      labels: [],
      datasets: [
        {
          label: "No Data Available",
          data: [],
        },
      ],
    };
  };

  const chartData = getChartData();

  // Render the chart or the list view
  const renderList = () => {
    if (status === "succeeded" && data) {
      let revenueData = [];
      if (groupBy === "month" && data.monthly_invoice_count) {
        revenueData = data.monthly_invoice_count.flatMap((yearData) =>
          yearData.monthly_invoice_count.map((monthData) => ({
            month: monthData.month,
            invoice_count: monthData.invoice_count,
          }))
        );
      } else if (groupBy === "year" && data.yearly_invoice_count) {
        revenueData = data.yearly_invoice_count.map((yearData) => ({
          year: yearData.year,
          invoice_count: yearData.invoice_count,
        }));
      }

      return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <h3 className="text-xl font-semibold mb-4">Invoice Pending Count Data</h3>
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left">Month/Year</th>
                <th className="px-4 py-2 text-left">Invoice Count</th>
              </tr>
            </thead>
            <tbody>
              {revenueData.map((item, index) => (
                <tr key={index}>
                  <td className="px-4 py-2">{item.month || item.year}</td>
                  <td className="px-4 py-2">{item.invoice_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return <div className="text-center text-gray-500">No data available</div>;
  };

  return (
    <div className="bg-white p-6 rounded-md shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold">Invoice Pending Count Report</h1>
        <div className="flex gap-4">
          <button
            onClick={() => setGroupBy("month")}
            className={`px-4 py-2 rounded-md ${groupBy === "month" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
          >
            Monthly
          </button>
          <button
            onClick={() => setGroupBy("year")}
            className={`px-4 py-2 rounded-md ${groupBy === "year" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"}`}
          >
            Yearly
          </button>
        </div>
      </div>

      {/* Report Type Dropdown */}
      <div className="flex gap-4">
        <div className="flex gap-4">
          <select
            className="px-4 py-2 rounded-md"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
          >
            <option value="Analytics">Analytics</option>
            <option value="List">List</option>
          </select>
        </div>

        <div>
          <button
            onClick={() => dispatch(fetchInvoicePendingCountReport(groupBy))}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Generate
          </button>
        </div>
      </div>

      {/* Loading, Error, or Data Display */}
      {status === "loading" && <p className="text-gray-500">Loading...</p>}
      {status === "failed" && <p className="text-red-500">{error}</p>}

      {/* Conditional Rendering of Chart or List */}
      {reportType === "Analytics" ? (
        <Bar data={chartData} options={{ responsive: true }} />
      ) : (
        renderList()
      )}
    </div>
  );
};

export default InvoicePendingCountChart;
